import { FC, useState } from 'react';
import { Delete, Visibility } from '@mui/icons-material';
import { numberToCurrencyString } from 'further-ui/utils';
import { UnrealisedCashFXGainOrLossTypeLabels } from 'further-ui/labels';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import TypographyButton from 'further-ui/components/TypographyButton';
import { SortOrder } from 'further-types/utils';
import getUnrealisedFxDisplayAmount from './getUnrealisedFxDisplayAmount';

const pageId = 'unrealised-cash-fx-history';

type Props = {
  vehicleId: string;
  periodId?: string;
  onViewRecord?: (recordId: string) => void;
};

type RecordToDelete = {
  id: string;
};

const CashHistory: FC<Props> = ({ vehicleId, periodId, onViewRecord }) => {
  const { vehicle } = useLayoutContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<RecordToDelete | null>(
    null,
  );

  const pagination = usePagination({
    id: pageId,
    order: SortOrder.Desc,
    orderBy: 'createdAt',
  });

  const { data, isLoading, refetch } =
    api.unrealisedCashFxGainOrLoss.listPosted.useQuery({
      vehicleId,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      order: pagination.order,
      orderBy: pagination.orderBy,
      excludePeriodId: periodId,
    });

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: deleteRecord, isPending: isDeleting } =
    api.unrealisedCashFxGainOrLoss.delete.useMutation(
      mutationOpts({
        successMessage: 'Record deleted successfully.',
        refetchType: 'active',
        onSuccess: () => {
          void refetch();
          handleCloseDeleteDialog();
        },
      }),
    );

  const handleOpenDeleteDialog = (record: RecordToDelete) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setRecordToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDelete = () => {
    if (!recordToDelete) return;

    deleteRecord({
      vehicleId,
      recordId: recordToDelete.id,
    });
  };

  const rows = data?.results ?? [];

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'ledgerDate',
      label: 'Ledger Date',
      render: (row) => row.ledgerDate?.toLabel?.() ?? '-',
    },
    {
      key: 'bankAccountName',
      label: 'Bank Account',
      render: (row) => row.bankAccountName ?? '-',
      disableSorting: true,
    },
    {
      key: 'gainOrLossType',
      label: 'Type',
      render: (row) => UnrealisedCashFXGainOrLossTypeLabels[row.gainOrLossType],
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) =>
        numberToCurrencyString(
          getUnrealisedFxDisplayAmount(row.gainOrLossType, row.amountLcy),
          {
            currency: vehicle?.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      disableSorting: true,
    },
  ];

  if (!vehicle || (!isLoading && !data?.results?.length)) {
    return null;
  }

  return (
    <Column spacing="lg" fullWidth>
      <TypographyButton
        text="Unrealised cash FX gains and losses history"
        textDecoration="none"
        onClick={() => setIsExpanded((prev) => !prev)}
        showExpandableStartAdornment
        isExpanded={isExpanded}
      />
      {isExpanded && (
        <Table
          columns={columns}
          rows={rows}
          isLoading={isLoading}
          actions={[
            {
              label: 'View',
              icon: Visibility,
              onClick: onViewRecord ? (row) => onViewRecord(row.id) : undefined,
            },
            {
              label: 'Delete',
              icon: Delete,
              disabled: (row) => !row.canDelete,
              tooltip: (row) =>
                row.canDelete ? undefined : row.deleteReason ?? 'Cannot delete',
              onClick: (row) =>
                handleOpenDeleteDialog({
                  id: row.id,
                }),
            },
          ]}
          pagination={{
            page: pagination.page,
            rowsPerPage: pagination.rowsPerPage,
            order: pagination.order,
            orderBy: pagination.orderBy,
            handleRequestSort: pagination.handleRequestSort,
            handleChangePage: pagination.handleChangePage,
            handleChangeRowsPerPage: pagination.handleChangeRowsPerPage,
          }}
          rowsTotalCount={data?.totalCount ?? 0}
        />
      )}
      <ConfirmDialog
        onClose={handleCloseDeleteDialog}
        open={deleteDialogOpen}
        title="Delete unrealised cash FX gain/loss?"
        content="Are you sure you want to delete this unrealised cash FX gain/loss? This cannot be undone."
        loading={isDeleting}
        actions={{
          confirm: {
            onClick: handleDelete,
            label: 'Delete',
          },
          cancel: {
            onClick: handleCloseDeleteDialog,
          },
        }}
      />
    </Column>
  );
};

export default CashHistory;
